import { detailCode, statusOf } from "@/lib/api-error";
import type { components } from "@/types/api.generated";
import apiClient from "./client";

/**
 * A decisão do arquiteto por objeto WRICEF (#1343): a F1 grava e lê, a F2 é a
 * seção "Decisão" no detalhe do objeto.
 *
 * Aliases sobre os tipos gerados do OpenAPI, como em `diagnostic-org-structure.ts`:
 * o contrato mora no backend, e um tipo escrito à mão divergiria dele em silêncio.
 */
export type WricefDecisionItem = components["schemas"]["WricefDecisionItem"];
export type WricefDecisionList = components["schemas"]["WricefDecisionList"];
export type WricefDecisionUpdate = components["schemas"]["WricefDecisionUpdate"];
export type WricefDecisionSummary = components["schemas"]["WricefDecisionSummary"];
export type WricefDecisionBulkUpdate = components["schemas"]["WricefDecisionBulkUpdate"];
export type WricefDecisionBulkResult = components["schemas"]["WricefDecisionBulkResult"];

/** O teto de objetos de um lote: o `max_length` de `obj_names` na rota. */
export const WRICEF_DECISION_BULK_MAX = 500;

/**
 * `GET /api/projects/{projectId}/wricef-decisions?obj_name=…` — a decisão de um objeto.
 *
 * `null` quando o objeto não vem na lista: a rota só lista objetos com letra WRICEF.
 */
export async function getWricefDecision(
  projectId: string,
  objName: string,
): Promise<WricefDecisionItem | null> {
  const res = await apiClient.get<WricefDecisionList>(
    `/api/projects/${projectId}/wricef-decisions`,
    { params: { obj_name: objName, limit: 1 } },
  );
  return res.data.items[0] ?? null;
}

/**
 * `GET /api/projects/{projectId}/wricef-decisions`: todos os objetos entregáveis do
 * projeto com a decisão de cada um, para o drill-down do Diagnóstico (#1477).
 *
 * Uma página só, no teto da rota (500): a AL5 tem 59 objetos e a Adeste 319.
 */
export async function listWricefDecisions(projectId: string): Promise<WricefDecisionList> {
  const res = await apiClient.get<WricefDecisionList>(
    `/api/projects/${projectId}/wricef-decisions`,
    { params: { limit: 500 } },
  );
  return res.data;
}

/**
 * `GET /api/projects/{projectId}/wricef-decisions/summary`: os totais por decisão,
 * para o topo do WRICEF Catalog (#1500, F3). São os números do card do Ato 1,
 * pela mesma conta, mais os ajustes e as horas de cada decisão.
 */
export async function getWricefDecisionSummary(projectId: string): Promise<WricefDecisionSummary> {
  const res = await apiClient.get<WricefDecisionSummary>(
    `/api/projects/${projectId}/wricef-decisions/summary`,
  );
  return res.data;
}

/**
 * `PUT /api/projects/{projectId}/wricef-decisions` — grava a decisão de um objeto.
 *
 * Só os campos enviados mudam, e `"decision": null` limpa a decisão e mantém o
 * critério. Devolve o item que o backend relê depois de gravar.
 */
export async function putWricefDecision(
  projectId: string,
  body: WricefDecisionUpdate,
): Promise<WricefDecisionItem> {
  const res = await apiClient.put<WricefDecisionItem>(
    `/api/projects/${projectId}/wricef-decisions`,
    body,
  );
  return res.data;
}

/**
 * `POST /api/projects/{projectId}/wricef-decisions/bulk` — a mesma decisão e o
 * mesmo critério em vários objetos, pela grade do WRICEF Catalog (#1501).
 *
 * Cada objeto passa pelas regras do `PUT`, e um recusado não derruba os outros:
 * ele volta em `rejected` com o código. Objeto repetido e scope item fora do
 * catálogo recusam o lote inteiro (422), porque valem para todos.
 */
export async function postWricefDecisionBulk(
  projectId: string,
  body: WricefDecisionBulkUpdate,
): Promise<WricefDecisionBulkResult> {
  const res = await apiClient.post<WricefDecisionBulkResult>(
    `/api/projects/${projectId}/wricef-decisions/bulk`,
    body,
  );
  return res.data;
}

/**
 * Os códigos com que o `PUT` da F1 e da F1b e o lote (#1501) recusam uma
 * gravação (`detail.code`). O lote usa os mesmos em cada `rejected`.
 */
const REFUSAL_CODES = [
  "object_not_in_catalog",
  "not_a_deliverable",
  "note_required",
  "standard_scope_item_required",
  "out_of_scope_reason_required",
  "gap_required",
  "related_scope_item_not_active",
  "unknown_scope_item",
  "decision_version_conflict",
  "repeated_object",
] as const;

/**
 * O que deu errado no `PUT`: um dos códigos da F1, `forbidden` (403: o papel
 * `user` vê e não edita) ou `failed`, o resto. Cai em `failed` também o `422`
 * do FastAPI, que não tem código, e a falha de rede, que não tem status.
 */
export type WricefDecisionErrorCode = (typeof REFUSAL_CODES)[number] | "forbidden" | "failed";

export function wricefDecisionErrorCode(error: unknown): WricefDecisionErrorCode {
  if (statusOf(error) === 403) return "forbidden";
  const code = detailCode(error);
  return REFUSAL_CODES.find((known) => known === code) ?? "failed";
}

export type WricefUsageRulePreview = components["schemas"]["UsageRulePreview"];

/**
 * `GET /api/projects/{projectId}/wricef-decisions/usage-rule`: o que a regra de
 * "Fora de escopo por falta de uso" marcaria agora (#1622), com a fonte, a
 * janela e a justificativa que gravaria em cada objeto.
 */
export async function getWricefUsageRule(projectId: string): Promise<WricefUsageRulePreview> {
  const res = await apiClient.get<WricefUsageRulePreview>(
    `/api/projects/${projectId}/wricef-decisions/usage-rule`,
  );
  return res.data;
}

/**
 * `POST /api/projects/{projectId}/wricef-decisions/usage-rule`: grava "Fora de
 * escopo" nos entregáveis que a regra marcou. Quem escolhe os objetos é o
 * servidor; a tela manda só a execução que a prévia mostrou, e o servidor recusa
 * com `usage_evidence_changed` se a medição mudou depois.
 */
export async function applyWricefUsageRule(
  projectId: string,
  executionId: string | null | undefined,
): Promise<WricefDecisionBulkResult> {
  const res = await apiClient.post<WricefDecisionBulkResult>(
    `/api/projects/${projectId}/wricef-decisions/usage-rule`,
    { execution_id: executionId ?? null },
  );
  return res.data;
}
